// react
import { SyntheticEvent, useCallback, useContext, useEffect, useMemo, useState } from 'react';

// types
import {
	ActionButton,
	Document,
	Entity,
	Loan,
	Pagination,
	Transaction,
	User,
} from '../../../@types';
import { entityTypesDefault } from '../../../@types/unions';

// i18n
import { useTranslation } from 'react-i18next';

// router
import {
	Link as RouterLink,
	Outlet,
	useLocation,
	useParams,
	useSearchParams,
	useNavigate,
} from 'react-router-dom';

// hooks
import { useFetch } from '../../../hooks/useFetch';

// context
import {
	ContextEntity,
	ContextEntityBeneficiaries,
	ContextEntityCashBenefits,
	ContextEntityDocuments,
	ContextEntityNonCashBenefits,
	ContextEntityOpEx,
	ContextEntityPayments,
	ContextEntitySuppliers,
	ContextEntityUsers,
} from './Entity.context';
import { ContextProject, ContextUserProjectPermissions } from '../Project/Project.context';

// mui
import { Grid } from '@mui/material';

// pages
import { Details } from './Details/Details';

// dialogs
import { DialogAddBeneficiary } from '../../dialogs/DialogAddBeneficiary/DialogAddBeneficiary';
import { DialogAddNonCashBenefit } from '../../dialogs/DialogAddNonCashBenefit/DialogAddNonCashBenefit';
import { DialogAddSupplier } from '../../dialogs/DialogAddSupplier/DialogAddSupplier';
import { DialogAddTransaction } from '../../dialogs/DialogAddTransaction/DialogAddTransaction';
import { DialogAddUser } from '../../dialogs/DialogAddUser/DialogAddUser';
import { DialogUploadDocsEntity } from '../../dialogs/DialogUploadDocsEntity/DialogUploadDocsEntity';

// ui
import { Breadcrumbs } from '../../ui/Breadcrumbs/Breadcrumbs';
import { Empty } from '../../ui/Empty/Empty';
import { entityIconTypes } from '../../ui/IconCircle/IconCircle';
import { Loading } from '../../ui/Loading/Loading';
import { Tabs } from '../../ui/Tabs/Tabs';
import { Title } from '../../ui/Title/Title';

// config
import { featureFlags } from '../../../config/featureFlags';

// defaults
import { defaultPagination } from '../../../utils/defaults';

// utils
import { translateType } from '../../../utils/translations';

export const PageEntity = () => {
	// session
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');

	// hooks
	const location = useLocation();
	const navigate = useNavigate();
	const { id, idEntity } = useParams();
	const [searchParams, setSearchParams] = useSearchParams();
	const { t } = useTranslation();

	// params
	const paramCategory = searchParams.get('category');
	const paramComponent = searchParams.get('component');
	const paramContract = searchParams.get('contract');
	const paramDialog = searchParams.get('dialog');
	const paramExpenseType = searchParams.get('expenseType');
	const paramLoan = searchParams.get('loan');
	const paramOrder = searchParams.get('order') || 'DESC';
	const paramOrderBy = searchParams.get('orderBy') || 'created';
	const paramPage = Number(searchParams.get('page') || 1);
	const paramQ = searchParams.get('q');
	const paramStatus = searchParams.get('status');
	const paramSupplier = searchParams.get('supplier');
	const paramTags = searchParams.get('tags');
	const paramType = searchParams.get('type');

	// context
	const { project } = useContext(ContextProject);
	const { userProjectPermissions } = useContext(ContextUserProjectPermissions);

	// defaults
	const tabs = useMemo(() => {
		const tabs = [];

		if (userProjectPermissions?.user?.read) {
			tabs.push({
				component: RouterLink,
				label: t('users'),
				to: `/project/${id}/entity/${idEntity}/users`,
				value: 'users',
			});
		}

		if (userProjectPermissions?.payment?.read) {
			tabs.push({
				component: RouterLink,
				label: t('payments'),
				to: `/project/${id}/entity/${idEntity}/payments`,
				value: 'payments',
			});
		}

		if (userProjectPermissions?.cashBenefit?.read) {
			tabs.push({
				component: RouterLink,
				label: t('cashBenefits'),
				to: `/project/${id}/entity/${idEntity}/cash-benefits`,
				value: 'cashBenefits',
			});
		}

		if (userProjectPermissions?.nonCashBenefit?.read) {
			tabs.push({
				component: RouterLink,
				label: t('nonCashBenefits'),
				to: `/project/${id}/entity/${idEntity}/non-cash-benefits`,
				value: 'nonCashBenefits',
			});
		}

		if (userProjectPermissions?.opEx?.read) {
			tabs.push({
				component: RouterLink,
				label: t('operatingExpenses'),
				to: `/project/${id}/entity/${idEntity}/opex`,
				value: 'opEx',
			});
		}

		if (userProjectPermissions?.supplier?.read) {
			tabs.push({
				component: RouterLink,
				label: t('suppliers'),
				to: `/project/${id}/entity/${idEntity}/suppliers`,
				value: 'suppliers',
			});
		}

		if (userProjectPermissions?.beneficiary?.read) {
			tabs.push({
				component: RouterLink,
				label: t('beneficiaries'),
				to: `/project/${id}/entity/${idEntity}/beneficiaries`,
				value: 'beneficiaries',
			});
		}

		if (userProjectPermissions?.document?.read) {
			tabs.push({
				component: RouterLink,
				label: t('documents'),
				to: `/project/${id}/entity/${idEntity}/documents`,
				value: 'documents',
			});
		}

		return tabs;
	}, [id, idEntity, t, userProjectPermissions]);

	const getTabByLocation = useCallback(
		(pathname) => {
			if (pathname.includes('beneficiaries')) {
				return 'beneficiaries';
			} else if (pathname.includes('documents')) {
				return 'documents';
			} else if (pathname.includes('non-cash-benefits')) {
				return 'nonCashBenefits';
			} else if (pathname.includes('opex')) {
				return 'opEx';
			} else if (pathname.includes('cash-benefits')) {
				return 'cashBenefits';
			} else if (pathname.includes('payments')) {
				return 'payments';
			} else if (pathname.includes('suppliers')) {
				return 'suppliers';
			} else if (pathname.includes('users')) {
				return 'users';
			} else {
				return tabs?.[0]?.value;
			}
		},
		[tabs]
	);

	const defaultTabValue = useMemo(() => {
		return getTabByLocation(location.pathname);
	}, [getTabByLocation, location]);

	// state
	const [beneficiaries, setBeneficiaries] = useState<Entity[]>([]);
	const [cashBenefits, setCashBenefits] = useState<Transaction[]>([]);
	const [documents, setDocuments] = useState<Document[]>([]);
	const [entity, setEntity] = useState<Entity | null>(null);
	const [loans, setLoans] = useState<Loan[]>([]);
	const [nonCashBenefits, setNonCashBenefits] = useState<Transaction[]>([]);
	const [opEx, setOpEx] = useState<Transaction[]>([]);

	const [paginationDocuments, setPaginationDocuments] = useState<Pagination>({
		...defaultPagination,
		page: paramPage,
	});
	const [paginationBeneficiaries, setPaginationBeneficiaries] = useState<Pagination>({
		...defaultPagination,
		page: paramPage,
	});
	const [paginationCashBenefits, setPaginationCashBenefits] = useState<Pagination>({
		...defaultPagination,
		page: paramPage,
	});
	const [paginationPayments, setPaginationPayments] = useState<Pagination>({
		...defaultPagination,
		page: paramPage,
	});
	const [paginationNonCashBenefits, setPaginationNonCashBenefits] = useState<Pagination>({
		...defaultPagination,
		page: paramPage,
	});
	const [paginationOpEx, setPaginationOpEx] = useState<Pagination>({
		...defaultPagination,
		page: paramPage,
	});
	const [paginationSuppliers, setPaginationSuppliers] = useState<Pagination>({
		...defaultPagination,
		page: paramPage,
	});
	const [paginationUsers, setPaginationUsers] = useState<Pagination>({
		...defaultPagination,
		page: paramPage,
	});
	const [payments, setPayments] = useState<Transaction[]>([]);
	const [suppliers, setSuppliers] = useState<Entity[]>([]);
	const [tabValue, setTabValue] = useState(defaultTabValue);
	const [users, setUsers] = useState<User[]>([]);

	// fetch entity
	const { fetchRequest: refetchEntity, isLoading: isLoadingEntity } = useFetch({
		isLazy: true,
		url: `${process.env.REACT_APP_API_URL}/entities/${idEntity}?project=${project?.id}&populate=wallet`,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data) {
				setEntity(res.data[0]);

				if (!entityTypesDefault.includes(res.data[0].type)) {
					setTabValue('users');
				}
			}
		},
	});

	// fetch entity loans
	const { isLoading: isLoadingLoans } = useFetch({
		isEnabled: Boolean(project),
		url: `${process.env.REACT_APP_API_URL}/loans?project=${project?.id}&entity=${idEntity}&populate=entities`,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res) {
				setLoans(res.data);
			}
		},
	});

	// fetch entity suppliers
	const {
		fetchRequest: fetchSuppliers,
		isLoading: isLoadingSuppliers,
		refetch: refetchSuppliers,
	} = useFetch({
		isLazy: true,
		url: `${process.env.REACT_APP_API_URL}/entities?project=${project?.id}&parent=${idEntity}&type=SUPPLIER&page=${paramPage}&limit=${paginationSuppliers.perPage}&order=${paramOrder}&orderBy=${paramOrderBy}`,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data) {
				setSuppliers(res.data);
			}

			if (res.pagination) {
				setPaginationSuppliers(res.pagination);
			}
		},
	});

	// fetch entity beneficiaries
	const {
		fetchRequest: fetchBeneficiaries,
		isLoading: isLoadingBeneficiaries,
		refetch: refetchBeneficiaries,
	} = useFetch({
		isLazy: true,
		url: `${process.env.REACT_APP_API_URL}/entities?project=${project?.id}&parent=${idEntity}&type=BENEFICIARY&page=${paramPage}&limit=${paginationBeneficiaries.perPage}&order=${paramOrder}&orderBy=${paramOrderBy}`,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data) {
				setBeneficiaries(res.data);
			}

			if (res.pagination) {
				setPaginationBeneficiaries(res.pagination);
			}
		},
	});

	// fetch entity payments
	let fetchPaymentsUrl = `${process.env.REACT_APP_API_URL}/transactions?project=${
		project?.id
	}&fromEntity=${idEntity}&type=${paramType || (featureFlags['DIRECT'] ? 'DIRECT,PAYMENT' : 'PAYMENT')}&page=${paramPage}&limit=${
		paginationUsers.perPage
	}&populate=category,component,contract,expenseType,fromAccount,payments.conversions,payments.loan,tags,toEntity&order=${paramOrder}&orderBy=${paramOrderBy}`;

	if (paramCategory) {
		fetchPaymentsUrl += `&category=${paramCategory}`;
	}
	if (paramComponent) {
		fetchPaymentsUrl += `&component=${paramComponent}`;
	}
	if (paramContract) {
		fetchPaymentsUrl += `&contract=${paramContract}`;
	}
	if (paramExpenseType) {
		fetchPaymentsUrl += `&expenseType=${paramExpenseType}`;
	}
	if (paramLoan) {
		fetchPaymentsUrl += `&loan=${paramLoan}`;
	}
	if (paramSupplier) {
		fetchPaymentsUrl += `&supplier=${paramSupplier}`;
	}
	if (paramStatus) {
		fetchPaymentsUrl += `&status=${paramStatus}`;
	}
	if (paramTags) {
		fetchPaymentsUrl += `&tags=${paramTags}`;
	}
	if (paramQ) {
		fetchPaymentsUrl += `&q=${paramQ}`;
	}

	const {
		fetchRequest: fetchPayments,
		isLoading: isLoadingPayments,
		refetch: refetchPayments,
	} = useFetch({
		isEnabled: Boolean(project),
		url: fetchPaymentsUrl,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data) {
				setPayments(res.data);
			}

			if (res.pagination) {
				setPaginationPayments(res.pagination);
			}
		},
	});

	// fetch entity cash benefits
	let fetchCashBenefitsUrl = `${process.env.REACT_APP_API_URL}/transactions?project=${project?.id}&page=${paramPage}&limit=${paginationCashBenefits.perPage}&type=CASH_BENEFIT&fromEntity=${idEntity}&populate=category,component,comments.user,expenseType,fromAccount,fromEntity,payments.conversions,payments.loan,tags,toEntity`;

	if (paramLoan) {
		fetchCashBenefitsUrl += `&loan=${paramLoan}`;
	}
	if (paramStatus) {
		fetchCashBenefitsUrl += `&status=${paramStatus}`;
	}
	if (paramQ) {
		fetchCashBenefitsUrl += `&q=${paramQ}`;
	}

	const {
		fetchRequest: fetchCashBenefits,
		isLoading: isLoadingCashBenefits,
		refetch: refetchCashBenefits,
	} = useFetch({
		isEnabled: Boolean(project),
		url: fetchCashBenefitsUrl,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data) {
				setCashBenefits(res.data);
			}

			if (res.pagination) {
				setPaginationCashBenefits(res.pagination);
			}
		},
	});

	// fetch entity non cash benefits
	let fetchNonCashBenefitsUrl = `${process.env.REACT_APP_API_URL}/transactions?fromEntity=${idEntity}&type=NON_CASH_BENEFIT&page=${paramPage}&limit=${paginationNonCashBenefits.perPage}&populate=benefit,fromAccount,fromEntity,tags,toEntity`;

	if (paramStatus) {
		fetchNonCashBenefitsUrl += `&status=${paramStatus}`;
	}
	if (paramQ) {
		fetchNonCashBenefitsUrl += `&q=${paramQ}`;
	}

	const {
		fetchRequest: fetchNonCashBenefits,
		isLoading: isLoadingNonCashBenefits,
		refetch: refetchNonCashBenefits,
	} = useFetch({
		isEnabled: Boolean(project),
		url: fetchNonCashBenefitsUrl,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data) {
				setNonCashBenefits(res.data);
			}

			if (res.pagination) {
				setPaginationNonCashBenefits(res.pagination);
			}
		},
	});

	// fetch entity opex
	let fetchOpExUrl = `${process.env.REACT_APP_API_URL}/transactions?project=${project?.id}&fromEntity=${idEntity}&type=OPEX&page=${paramPage}&limit=${paginationOpEx.perPage}&populate=category,component,expenseType,fromAccount,fromEntity,payments.loan,tags,toEntity&order=${paramOrder}&orderBy=${paramOrderBy}`;

	if (paramCategory) {
		fetchOpExUrl += `&category=${paramCategory}`;
	}
	if (paramComponent) {
		fetchOpExUrl += `&component=${paramComponent}`;
	}
	if (paramExpenseType) {
		fetchOpExUrl += `&expenseType=${paramExpenseType}`;
	}
	if (paramQ) {
		fetchOpExUrl += `&q=${paramQ}`;
	}
	if (paramTags) {
		fetchOpExUrl += `&tags=${paramTags}`;
	}

	const {
		fetchRequest: fetchOpEx,
		isLoading: isLoadingOpEx,
		refetch: refetchOpEx,
	} = useFetch({
		isEnabled: Boolean(project),
		url: fetchOpExUrl,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data) {
				setOpEx(res.data);
			}

			if (res.pagination) {
				setPaginationOpEx(res.pagination);
			}
		},
	});

	// fetch entity users
	let fetchUsersUrl = `${process.env.REACT_APP_API_URL}/users?projects=${project?.id}&entities=${idEntity}&populate=country,projects.permissions&page=${paramPage}&limit=${paginationUsers.perPage}&order=${paramOrder}&orderBy=${paramOrderBy}`;

	if (paramQ) {
		fetchUsersUrl += `&q=${paramQ}`;
	}

	const {
		fetchRequest: fetchUsers,
		isLoading: isLoadingUsers,
		refetch: refetchUsers,
	} = useFetch({
		isLazy: true,
		url: fetchUsersUrl,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data) {
				setUsers(res.data);
			}

			if (res.pagination) {
				setPaginationUsers(res.pagination);
			}
		},
	});

	// fetch entity documents
	const {
		fetchRequest: fetchDocuments,
		isLoading: isLoadingDocuments,
		refetch: refetchDocuments,
	} = useFetch({
		isLazy: true,
		url: `${process.env.REACT_APP_API_URL}/documents?project=${project?.id}&entities=${idEntity}&page=${paramPage}&limit=${paginationDocuments.perPage}&fields=contract,created,entities,notarizationHash,id,name,transaction,type,user&populate=contract,transaction,type,user`,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data) {
				setDocuments(res.data);
			}

			if (res.pagination) {
				setPaginationDocuments(res.pagination);
			}
		},
	});

	// title actions
	const titleActions: ActionButton[] = [];
	if (tabValue === 'payments' && userProjectPermissions?.payment?.create) {
		titleActions.push({
			children: t('recordPayment'),
			onClick: () => {
				searchParams.set('dialog', 'recordPayment');
				setSearchParams(searchParams);
			},
			variant: 'contained',
		});
	}

	if (tabValue === 'cashBenefits' && userProjectPermissions?.cashBenefit?.create) {
		titleActions.push({
			children: t('recordCashBenefit'),
			onClick: () => {
				searchParams.set('dialog', 'recordCashBenefit');
				setSearchParams(searchParams);
			},
			variant: 'contained',
		});
	}

	if (tabValue === 'nonCashBenefits' && userProjectPermissions?.nonCashBenefit?.create) {
		titleActions.push({
			children: t('recordNonCashBenefit'),
			onClick: () => {
				searchParams.set('dialog', 'recordNonCashBenefit');
				setSearchParams(searchParams);
			},
			variant: 'contained',
		});
	}

	if (tabValue === 'opEx' && userProjectPermissions?.opEx?.create) {
		titleActions.push({
			children: t('recordOpEx'),
			onClick: () => {
				searchParams.set('dialog', 'recordOpEx');
				setSearchParams(searchParams);
			},
			variant: 'contained',
		});
	}

	if (tabValue === 'users' && userProjectPermissions?.user?.create) {
		titleActions.push({
			children: t('addUser'),
			onClick: () => {
				searchParams.set('dialog', 'addUser');
				setSearchParams(searchParams);
			},
			variant: 'contained',
		});
	}

	if (tabValue === 'suppliers' && userProjectPermissions?.supplier?.create) {
		titleActions.push({
			children: t('addSupplier'),
			onClick: () => {
				searchParams.set('dialog', 'addSupplier');
				setSearchParams(searchParams);
			},
			variant: 'contained',
		});
	}

	if (tabValue === 'beneficiaries' && userProjectPermissions?.beneficiary?.create) {
		titleActions.push({
			children: t('addBeneficiary'),
			onClick: () => {
				searchParams.set('dialog', 'addBeneficiary');
				setSearchParams(searchParams);
			},
			variant: 'contained',
		});
	}

	if (tabValue === 'documents' && userProjectPermissions?.document?.create) {
		titleActions.push({
			children: t('uploadDocuments'),
			onClick: () => {
				searchParams.set('dialog', 'uploadDocsEntity');
				setSearchParams(searchParams);
			},
			variant: 'contained',
		});
	}

	const handleChangeTab = (e: SyntheticEvent, value: string) => {
		setTabValue(value);
	};

	const handleCloseDialog = () => {
		setSearchParams((params) => {
			params.delete('dialog');
			params.delete('dialogSupplierName');
			params.delete('dialogToEntity');
			return params;
		});
	};

	let template;

	if (isLoadingEntity) {
		template = <Loading variant="page" />;
	} else {
		if (entity) {
			template = (
				<>
					<Grid container spacing={2}>
						<Grid item xs={11} lg={12}>
							<Breadcrumbs
								crumbs={[
									{
										children: t('projects'),
										to: '/projects',
									},
									{
										children: project?.idWbg,
										to: `/project/${id}`,
									},
									{
										children: t('entities'),
										to: `/project/${id}/entities`,
									},
								]}
								page={entity.name}
							/>
						</Grid>
						<Grid item xs={12}>
							<Title
								actions={titleActions}
								color="magenta"
								ChipStatusProps={{
									status: entity?.wallet?.status || 'ACTIVE',
								}}
								IconCircleProps={{
									type: entityIconTypes[entity.type],
								}}
								overline={translateType(entity.type, t)}
								title={entity.name}
							/>
						</Grid>
						<Grid item xs={12}>
							<Details entity={entity} isLoadingLoans={isLoadingLoans} loans={loans} />
						</Grid>
						<Grid item xs={12}>
							<Tabs onChange={handleChangeTab} tabs={tabs} value={tabValue} hasBorder={false} />
						</Grid>
						<Grid item xs={12}>
							<Outlet />
						</Grid>
					</Grid>

					<DialogAddTransaction
						isOpen={paramDialog === 'recordPayment'}
						onClose={handleCloseDialog}
						onSuccess={() => {
							refetchPayments();
						}}
						type="PAYMENT"
					/>

					<DialogAddTransaction
						isOpen={paramDialog === 'recordCashBenefit'}
						onClose={handleCloseDialog}
						onSuccess={() => {
							refetchCashBenefits();
						}}
						type="CASH_BENEFIT"
					/>

					<DialogAddNonCashBenefit
						isOpen={paramDialog === 'recordNonCashBenefit'}
						onClose={handleCloseDialog}
						onSuccess={() => {
							refetchNonCashBenefits();
						}}
					/>

					<DialogAddTransaction
						isOpen={paramDialog === 'recordOpEx'}
						onClose={handleCloseDialog}
						onSuccess={() => {
							refetchOpEx();
						}}
						type="OPEX"
					/>

					<DialogAddUser
						entity={entity}
						isOpen={paramDialog === 'addUser'}
						onClose={handleCloseDialog}
						onSuccess={() => {
							refetchUsers();
						}}
					/>
					<DialogAddSupplier
						isOpen={paramDialog === 'addSupplier'}
						onClose={handleCloseDialog}
						onSuccess={() => {
							refetchSuppliers();
						}}
					/>
					<DialogAddBeneficiary
						isOpen={paramDialog === 'addBeneficiary'}
						onClose={handleCloseDialog}
						onSuccess={() => {
							refetchBeneficiaries();
						}}
					/>
					<DialogUploadDocsEntity
						isOpen={paramDialog === 'uploadDocsEntity'}
						onClose={handleCloseDialog}
						onSuccess={() => {
							refetchDocuments();
						}}
						entity={entity}
					/>
				</>
			);
		} else {
			template = (
				<Empty
					actions={[
						{
							children: t('entities'),
							component: RouterLink,
							to: `/project/${project?.id}/entities`,
							variant: 'contained',
						},
					]}
					content={{ children: t('empty.entity.content') }}
					title={{ children: t('empty.entity.title') }}
					IconCircleProps={{ type: 'entity' }}
					variant="page"
				/>
			);
		}
	}

	// fetch entity when idEntity changes
	useEffect(() => {
		if (project) {
			refetchEntity();
		}
	}, [idEntity, project, refetchEntity]);

	// fetch users when page changes
	useEffect(() => {
		if (project) {
			if (tabValue === 'payments') {
				fetchPayments();
			} else if (tabValue === 'cashBenefits') {
				fetchCashBenefits();
			} else if (tabValue === 'nonCashBenefits') {
				fetchNonCashBenefits();
			} else if (tabValue === 'opEx') {
				fetchOpEx();
			} else if (tabValue === 'suppliers') {
				fetchSuppliers();
			} else if (tabValue === 'users') {
				fetchUsers();
			} else if (tabValue === 'beneficiaries') {
				fetchBeneficiaries();
			} else if (tabValue === 'documents') {
				fetchDocuments();
			}
		}
	}, [
		fetchBeneficiaries,
		fetchCashBenefits,
		fetchNonCashBenefits,
		fetchOpEx,
		fetchDocuments,
		fetchPayments,
		fetchSuppliers,
		fetchUsers,
		paramCategory,
		paramComponent,
		paramContract,
		paramExpenseType,
		paramLoan,
		paramOrder,
		paramOrderBy,
		paramPage,
		paramQ,
		paramStatus,
		paramSupplier,
		paramTags,
		paramType,
		project,
		tabValue,
	]);

	// set active tab when location changes
	useEffect(() => {
		const tabValueByLocation = getTabByLocation(location.pathname);
		setTabValue(tabValueByLocation);
	}, [getTabByLocation, location]);

	// navigate to first tab if path is not set
	useEffect(() => {
		const segments = location.pathname.split('/');
		if (segments.length < 6) {
			navigate(tabs[0].to);
		}
	}, [location, navigate, tabs]);

	return (
		<ContextEntity.Provider value={{ entity, isLoadingEntity, refetchEntity, setEntity }}>
			<ContextEntityPayments.Provider
				value={{
					isLoadingPayments,
					paginationPayments,
					payments,
					refetchPayments,
					setPaginationPayments,
					setPayments,
				}}>
				<ContextEntityCashBenefits.Provider
					value={{
						isLoadingCashBenefits,
						paginationCashBenefits,
						cashBenefits,
						refetchCashBenefits,
						setCashBenefits,
						setPaginationCashBenefits,
					}}>
					<ContextEntityNonCashBenefits.Provider
						value={{
							isLoadingNonCashBenefits,
							paginationNonCashBenefits,
							nonCashBenefits,
							refetchNonCashBenefits,
							setNonCashBenefits,
							setPaginationNonCashBenefits,
						}}>
						<ContextEntityOpEx.Provider
							value={{
								isLoadingOpEx,
								opEx,
								paginationOpEx,
								refetchOpEx,
								setOpEx,
								setPaginationOpEx,
							}}>
							<ContextEntitySuppliers.Provider
								value={{
									isLoadingSuppliers,
									paginationSuppliers,
									refetchSuppliers,
									setPaginationSuppliers,
									setSuppliers,
									suppliers,
								}}>
								<ContextEntityUsers.Provider
									value={{
										isLoadingUsers,
										paginationUsers,
										refetchUsers,
										setPaginationUsers,
										setUsers,
										users,
									}}>
									<ContextEntityBeneficiaries.Provider
										value={{
											beneficiaries,
											isLoadingBeneficiaries,
											paginationBeneficiaries,
											refetchBeneficiaries,
											setBeneficiaries,
											setPaginationBeneficiaries,
										}}>
										<ContextEntityDocuments.Provider
											value={{
												documents,
												isLoadingDocuments,
												paginationDocuments,
												refetchDocuments,
												setDocuments,
												setPaginationDocuments,
											}}>
											{template}
										</ContextEntityDocuments.Provider>
									</ContextEntityBeneficiaries.Provider>
								</ContextEntityUsers.Provider>
							</ContextEntitySuppliers.Provider>
						</ContextEntityOpEx.Provider>
					</ContextEntityNonCashBenefits.Provider>
				</ContextEntityCashBenefits.Provider>
			</ContextEntityPayments.Provider>
		</ContextEntity.Provider>
	);
};
